import { Injectable } from '@angular/core';
import { mixPanelEvent } from '@features/activities/data';
import { trans } from '@mongez/localization';
import { hardNavigate } from '@presentation/shared/router';
import { toastError, toastSuccess } from '@presentation/shared/toast';
import { GetRecommendationsUseCase } from 'app/core/usecases/get-recommendations.usecase';
import { BasePresenter } from 'app/presentation/base/base.presenter';
import { RECOMMENDATIONS_URL } from 'app/presentation/shared/constants';
import { CatalogService } from 'app/presentation/shared/services/catalog.service';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { RecommendationsSideEffect } from './recommendations.sideEffects';
import { RecommendationsViewEvent } from './recommendations.viewEvents';
import { RecommendationsViewState } from './recommendations.viewState';

@Injectable({
  providedIn: 'root',
})
export class RecommendationsPresenter extends BasePresenter<
  RecommendationsViewState,
  RecommendationsViewEvent,
  RecommendationsSideEffect
> {
  private viewStateSubject = new BehaviorSubject<RecommendationsViewState>({
    recommendations: [],
    isLoading: false,
  });

  viewState$: Observable<RecommendationsViewState> = this.viewStateSubject.asObservable();

  constructor(
    private getRecommendationsUseCase: GetRecommendationsUseCase,
    private catalogService: CatalogService,
  ) {
    super();
  }

  defaultViewState(): RecommendationsViewState {
    return {
      recommendations: [],
      isLoading: false,
    };
  }

  protected onViewEvent(event: RecommendationsViewEvent): void {
    switch (event.type) {
      case 'Init':
        this.loadRecommendations();
        break;
      case 'ToggleCatalog':
        if (event.product.isAddedToCatalog) {
          mixPanelEvent('uncatalog_recommended_product', { 'Product Id': event.product.sku });

          this.catalogService.uncatalogProduct(event.product.id).subscribe({
            next: () => {
              toastSuccess(trans('RECOMMENDATIONS.PRODUCT_REMOVED_FROM_CATALOG'));
            },
            error: () => {
              toastError(trans('RECOMMENDATIONS.GENERIC_ERROR_MESSAGE'));
            },
          });
        } else {
          mixPanelEvent('catalog_recommended_product', { 'Product Id': event.product.sku });

          this.catalogService.catalogProduct(event.product.id).subscribe({
            next: () => {
              toastSuccess(trans('RECOMMENDATIONS.PRODUCT_ADDED_TO_CATALOG'));
            },
            error: () => {
              toastError(trans('RECOMMENDATIONS.GENERIC_ERROR_MESSAGE'));
            },
          });
        }

        this.viewStateSubject.next({
          ...this.viewStateSubject.value,
          recommendations: this.viewStateSubject.value.recommendations.map((recommendation) => ({
            ...recommendation,
            recommendedProducts: recommendation.recommendedProducts.map((updatedProduct) =>
              updatedProduct.id === event.product.id
                ? { ...updatedProduct, isAddedToCatalog: !updatedProduct.isAddedToCatalog }
                : updatedProduct,
            ),
          })),
        });
        break;

      case 'ViewAllRecommendations':
        mixPanelEvent('view_recommendations_page', { source: 'home' });
        hardNavigate(RECOMMENDATIONS_URL);
        break;
    }
  }

  async loadRecommendations(): Promise<void> {
    this.viewStateSubject.next({
      ...this.viewStateSubject.value,
      isLoading: true,
    });

    try {
      const recommendations = await firstValueFrom(this.getRecommendationsUseCase.execute());

      this.viewStateSubject.next({
        ...this.viewStateSubject.value,
        recommendations,
        isLoading: false,
      });
    } catch (error) {
      this.viewStateSubject.next({
        ...this.viewStateSubject.value,
        isLoading: false,
      });
    }
  }
}
